import React, { useState, useEffect } from 'react'
import azmLogo from '../Images/logo-azm-2.png';
import { Link } from 'react-router-dom';
import GeneralService from '../../services/general.service';

export default function Navbar() {
  const [openingHours, setOpeningHours] = useState('Loading...');
  const [isOpen, setIsOpen] = useState(false);
  const [showMobileNotification, setShowMobileNotification] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [hoursData, setHoursData] = useState(null);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);

  useEffect(() => {
    fetchOpeningHours();
    
    // Check if open every minute
    const interval = setInterval(checkIfOpen, 60000);
    
    // Mobile notification timer - show for 4 seconds, hide for 6 seconds
    const notificationInterval = setInterval(() => {
      setShowMobileNotification(prev => !prev);
    }, showMobileNotification ? 4000 : 6000);
    
    return () => {
      clearInterval(interval);
      clearInterval(notificationInterval);
    };
  }, [showMobileNotification]);

  // Check if open whenever hoursData changes
  useEffect(() => {
    if (hoursData) {
      checkIfOpen();
    }
  }, [hoursData]);

  // Toggle navbar function
  const toggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen);
  };

  // Close navbar when clicking on a link
  const closeNavbar = () => {
    setIsNavbarOpen(false);
  };

const checkIfOpen = () => {
  const now = new Date();
  const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTime = currentHour * 60 + currentMinute;
  
  console.log('=== CHECKING IF OPEN ===');
  console.log('Current day:', currentDay);
  console.log('Current time:', `${currentHour}:${currentMinute}`);
  console.log('Current time in minutes:', currentTime);
  console.log('Hours data:', hoursData);
  
  // If we have API data, use it
  if (hoursData && Array.isArray(hoursData)) {
    let todaySchedule = null;
    
    // Find today's schedule
    for (const schedule of hoursData) {
      console.log('Checking schedule:', schedule);
      
      // Check if day_range exists and includes today
      if (schedule.day_range) {
        const days = parseDayRange(schedule.day_range);
        console.log('Parsed days from range:', days);
        if (days.includes(currentDay)) {
          todaySchedule = schedule;
          console.log('Found matching schedule by day_range:', todaySchedule);
          break;
        }
      }
      
      // Check if individual day matches
      if (schedule.day && schedule.day === currentDay) {
        todaySchedule = schedule;
        console.log('Found matching schedule by individual day:', todaySchedule);
        break;
      }
    }
    
    console.log('Final today\'s schedule:', todaySchedule);
    
    if (todaySchedule) {
      // Check if closed
      if (todaySchedule.is_closed) {
        console.log('Closed today according to schedule');
        setIsOpen(false);
        return;
      }
      
      // Check opening hours
      if (todaySchedule.open_time && todaySchedule.close_time) {
        const openTime = timeToMinutes(todaySchedule.open_time);
        const closeTime = timeToMinutes(todaySchedule.close_time);
        
        console.log('=== TIME COMPARISON DEBUG ===');
        console.log('Open time string:', todaySchedule.open_time);
        console.log('Close time string:', todaySchedule.close_time);
        console.log('Open time in minutes:', openTime);
        console.log('Close time in minutes:', closeTime);
        console.log('Current time in minutes:', currentTime);
        console.log('Is after open time:', currentTime >= openTime);
        console.log('Is before close time:', currentTime < closeTime);
        console.log('Final result (should be open):', currentTime >= openTime && currentTime < closeTime);
        
        const shouldBeOpen = currentTime >= openTime && currentTime < closeTime;
        console.log('Setting isOpen to:', shouldBeOpen);
        setIsOpen(shouldBeOpen);
        return;
      }
    } else {
      console.log('No schedule found for today, checking if this is an "always open" or default case');
      
      // If no specific schedule found but we have data, 
      // check if there's a general schedule that applies to all days
      const generalSchedule = hoursData.find(schedule => 
        !schedule.day && !schedule.day_range && schedule.open_time && schedule.close_time
      );
      
      if (generalSchedule) {
        console.log('Using general schedule:', generalSchedule);
        const openTime = timeToMinutes(generalSchedule.open_time);
        const closeTime = timeToMinutes(generalSchedule.close_time);
        const shouldBeOpen = currentTime >= openTime && currentTime < closeTime;
        console.log('General schedule - setting isOpen to:', shouldBeOpen);
        setIsOpen(shouldBeOpen);
        return;
      }
    }
  }
  
  // Fallback to default hours if no API data or no matching schedule
  console.log('Using fallback hours check (9 AM - 10 PM)');
  const openTime = 9 * 60; // 9:00 in minutes
  const closeTime = 22 * 60; // 22:00 (10 PM) in minutes
  
  const shouldBeOpen = currentTime >= openTime && currentTime < closeTime;
  console.log('Fallback - setting isOpen to:', shouldBeOpen);
  setIsOpen(shouldBeOpen);
};

  // Helper function to convert time string to minutes
const timeToMinutes = (timeString) => {
  console.log('=== TIME TO MINUTES CONVERSION ===');
  console.log('Input timeString:', timeString);
  
  if (!timeString || typeof timeString !== 'string') {
    console.warn('❌ Invalid time string:', timeString);
    return 0;
  }
  
  try {
    const parts = timeString.split(':');
    console.log('Split parts:', parts);
    
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1] || '0', 10);
    // Note: We ignore seconds (parts[2]) as they're not needed for minute calculation
    const totalMinutes = hours * 60 + minutes;
    
    console.log(`Conversion: ${timeString}`);
    console.log(`  Hours: ${hours}`);
    console.log(`  Minutes: ${minutes}`);
    console.log(`  Total Minutes: ${totalMinutes}`);
    console.log(`  Verify: ${Math.floor(totalMinutes/60)}:${(totalMinutes%60).toString().padStart(2,'0')}`);
    
    return totalMinutes;
  } catch (error) {
    console.error('❌ Error parsing time:', error);
    return 0;
  }
};

  // Helper function to parse day ranges like "Monday - Friday" or "Monday, Wednesday"
// Helper function to parse day ranges like "Monday - Friday" or "Monday, Wednesday"
const parseDayRange = (dayRange) => {
  console.log('=== PARSING DAY RANGE ===');
  console.log('Input dayRange:', dayRange);
  
  if (!dayRange) {
    console.log('No day range provided');
    return [];
  }
  
  const dayOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Handle single day
  if (!dayRange.includes('-') && !dayRange.includes(',')) {
    console.log('Single day detected:', dayRange.trim());
    return [dayRange.trim()];
  }
  
  // Handle comma-separated days
  if (dayRange.includes(',') && !dayRange.includes('-')) {
    const days = dayRange.split(',').map(day => day.trim());
    console.log('Comma-separated days:', days);
    return days;
  }
  
  // Handle day ranges like "Monday - Friday" or "Monday - Sunday"
  if (dayRange.includes('-')) {
    const parts = dayRange.split('-').map(part => part.trim());
    console.log('Range parts:', parts);
    
    if (parts.length === 2) {
      const startIndex = dayOrder.indexOf(parts[0]);
      const endIndex = dayOrder.indexOf(parts[1]);
      
      console.log('Start day:', parts[0], 'Start index:', startIndex);
      console.log('End day:', parts[1], 'End index:', endIndex);
      
      if (startIndex !== -1 && endIndex !== -1) {
        const days = [];
        
        // Special case: if start is Monday (1) and end is Sunday (0), it means Monday-Sunday (all week)
        if (parts[0] === 'Monday' && parts[1] === 'Sunday') {
          console.log('Detected Monday - Sunday range (full week)');
          return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        }
        
        // Handle normal ranges
        if (startIndex <= endIndex) {
          for (let i = startIndex; i <= endIndex; i++) {
            days.push(dayOrder[i]);
          }
        } else {
          // Handle wrap-around (e.g., Saturday - Monday)
          for (let i = startIndex; i < dayOrder.length; i++) {
            days.push(dayOrder[i]);
          }
          for (let i = 0; i <= endIndex; i++) {
            days.push(dayOrder[i]);
          }
        }
        
        console.log('Generated day range:', days);
        return days;
      }
    }
  }
  
  console.log('Could not parse day range, returning empty array');
  return [];
};

  const fetchOpeningHours = async () => {
    try {
      console.log('=== FETCHING OPENING HOURS ===');
      const response = await GeneralService.getOpeningHours();
      
      console.log('API Response:', response);
      console.log('Response success:', response?.success);
      console.log('Response data:', response?.data);
      
      if (response && response.success && response.data) {
        console.log('API data length:', response.data.length);
        console.log('API data structure:', response.data);
        
        if (Array.isArray(response.data) && response.data.length > 0) {
          setHoursData(response.data);
          
          const formattedHours = formatOpeningHours(response.data);
          console.log('Formatted hours:', formattedHours);
          setOpeningHours(formattedHours);
          setApiError(false);
        } else {
          console.warn('API returned empty or invalid data array');
          setDefaultHours();
        }
      } else {
        console.warn('API response structure is invalid:', {
          hasResponse: !!response,
          hasSuccess: response?.success,
          hasData: response?.data
        });
        setDefaultHours();
      }
    } catch (error) {
      console.error('Error fetching opening hours:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      setDefaultHours();
    }
  };

  const setDefaultHours = () => {
    console.log('Setting default opening hours');
    setOpeningHours('Monday - Sunday: 9.00 am - 10.00 pm');
    setHoursData(null);
    setApiError(true);
  };

  const formatOpeningHours = (hours) => {
    console.log('=== FORMATTING OPENING HOURS ===');
    console.log('Input hours:', hours);
    
    if (!hours || !Array.isArray(hours) || hours.length === 0) {
      console.warn('Invalid hours data provided to formatOpeningHours');
      return 'Mon - Sat: 9.00 am - 10.00 pm, Sun: Closed';
    }

    const result = [];
    
    hours.forEach((hour, index) => {
      console.log(`Processing hour ${index}:`, hour);
      
      if (!hour || typeof hour !== 'object') {
        console.warn(`Invalid hour object at index ${index}:`, hour);
        return;
      }
      
      if (hour.day_range) {
        if (hour.is_closed) {
          const status = hour.emergency_only ? 'Closed (Emergency Only)' : 'Closed';
          result.push(`${hour.day_range}: ${status}`);
        } else if (hour.formatted_time) {
          result.push(`${hour.day_range}: ${hour.formatted_time}`);
        } else if (hour.open_time && hour.close_time) {
          const openTime = formatTime(hour.open_time);
          const closeTime = formatTime(hour.close_time);
          result.push(`${hour.day_range}: ${openTime} - ${closeTime}`);
        }
      }
      else if (hour.day) {
        if (hour.is_closed) {
          const status = hour.emergency_only ? 'Closed (Emergency Only)' : 'Closed';
          result.push(`${hour.day.substring(0, 3)}: ${status}`);
        } else if (hour.open_time && hour.close_time) {
          const openTime = formatTime(hour.open_time);
          const closeTime = formatTime(hour.close_time);
          result.push(`${hour.day.substring(0, 3)}: ${openTime} - ${closeTime}`);
        }
      }
      // Handle case where there's no specific day/day_range (general hours)
      else if (hour.open_time && hour.close_time && !hour.day && !hour.day_range) {
        const openTime = formatTime(hour.open_time);
        const closeTime = formatTime(hour.close_time);
        result.push(`Daily: ${openTime} - ${closeTime}`);
      }
    });

    console.log('Final formatted result:', result);

    if (result.length === 0) {
      console.warn('No formatted hours available, using default');
      return 'Mon - Sat: 9.00 am - 10.00 pm, Sun: Closed';
    }

    return result.join(', ');
  };

  const formatTime = (time) => {
    if (!time || typeof time !== 'string') {
      console.warn('Invalid time format:', time);
      return 'Invalid Time';
    }
    
    try {
      const timeParts = time.split(':');
      const hours = timeParts[0];
      const minutes = timeParts[1] || '00';
      
      const hourNum = parseInt(hours, 10);
      const hour12 = hourNum % 12 || 12;
      const ampm = hourNum >= 12 ? 'pm' : 'am';
      return `${hour12}.${minutes.padStart(2, '0')} ${ampm}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid Time';
    }
  };

  const formatDayRange = (days) => {
    if (!Array.isArray(days) || days.length === 0) {
      return 'No Days';
    }
    
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const sortedDays = days.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

    if (sortedDays.length === 1) {
      return sortedDays[0].substring(0, 3);
    }

    const consecutive = [];
    let start = 0;

    for (let i = 1; i <= sortedDays.length; i++) {
      if (i === sortedDays.length || 
          dayOrder.indexOf(sortedDays[i]) !== dayOrder.indexOf(sortedDays[i-1]) + 1) {
        
        if (i - start === 1) {
          consecutive.push(sortedDays[start].substring(0, 3));
        } else {
          consecutive.push(`${sortedDays[start].substring(0, 3)} - ${sortedDays[i-1].substring(0, 3)}`);
        }
        start = i;
      }
    }

    return consecutive.join(', ');
  };

  const closeMobileNotification = () => {
    setShowMobileNotification(false);
  };

  const retryFetchHours = () => {
    setOpeningHours('Loading...');
    fetchOpeningHours();
  };

  return (
    <>
      {/* Debug Info - Remove this in production */}
      {process.env.NODE_ENV === 'development' && apiError && (
        <div style={{
          background: '#ff6b6b',
          color: 'white',
          padding: '8px',
          textAlign: 'center',
          fontSize: '12px'
        }}>
          API Error: Using default hours. 
          <button 
            onClick={retryFetchHours}
            style={{
              marginLeft: '10px',
              padding: '2px 8px',
              background: 'white',
              color: '#ff6b6b',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      )}
  
      {/* Desktop Topbar */}
      <div className="container-fluid topbar-enhanced ps-4 pe-0 d-none d-lg-block">
        <div className="row gx-0 align-items-center">
          <div className="col-lg-7 text-center text-lg-start">
            <div className="d-inline-flex align-items-center">
              <div className="opening-hours-container">
                <i className="far fa-clock clock-icon"></i>
                <span className="hours-text">
                  Opening Hours: {openingHours}
                </span>
                <span className={`status-badge ${isOpen ? 'status-open' : 'status-closed'}`}>
                  {isOpen ? '🟢 OPEN NOW' : '🔴 CLOSED'}
                </span>
              </div>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="contact-info d-flex align-items-center text-white px-4">
              <div className="contact-item flex-fill text-center me-3">
                <p className="m-0">
                  <i className="fa fa-envelope-open me-2"></i>
                  zubairmukhtar14@gmail.com
                </p>
              </div>
              <div className="contact-item flex-fill text-center">
                <p className="m-0">
                  <i className="fa fa-phone-alt me-2"></i>
                  03009605574
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Notification */}
      {showMobileNotification && (
        <div className="mobile-notification d-lg-none">
          <div className="mobile-notification-content">
            <div className="mobile-notification-text">
              <i className="far fa-clock mobile-notification-icon"></i>
              {openingHours}
              <span className={`mobile-notification-status ${isOpen ? 'status-open' : 'status-closed'}`}>
                {isOpen ? '🟢 OPEN' : '🔴 CLOSED'}
              </span>
            </div>
            <button
              className="mobile-notification-close"
              onClick={closeMobileNotification}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main Navbar */}
      <nav className="navbar navbar-expand-lg navbar-enhanced px-4">
        <div className="container-fluid">
          <Link to="/" className="navbar-brand">
            <div className="logo-container">
              <img src={azmLogo} alt="AZM Logo" className="logo-img" />
              <h1 className="brand-text d-none d-sm-block">Azm Rehab Centre</h1>
            </div>
          </Link>
          
          {/* Custom Navbar Toggler with manual control */}
          <button
            className="navbar-toggler"
            type="button"
            onClick={toggleNavbar}
            aria-controls="navbarCollapse"
            aria-expanded={isNavbarOpen}
            aria-label="Toggle navigation"
          >
            {/* Custom hamburger icon */}
            <div className="hamburger-icon">
              <span className={`bar ${isNavbarOpen ? 'bar-1-active' : ''}`}></span>
              <span className={`bar ${isNavbarOpen ? 'bar-2-active' : ''}`}></span>
              <span className={`bar ${isNavbarOpen ? 'bar-3-active' : ''}`}></span>
            </div>
          </button>
          
          {/* Navbar collapse with manual control */}
          <div className={`collapse navbar-collapse ${isNavbarOpen ? 'show' : ''}`} id="navbarCollapse">
            <div className="navbar-nav ms-auto">
              <Link to="/" className="nav-item nav-link active" onClick={closeNavbar}>Home</Link>
              <Link to="/service" className="nav-item nav-link" onClick={closeNavbar}>Services</Link>
              <Link to="/staff-team" className="nav-item nav-link" onClick={closeNavbar}>Staff</Link>
              <Link to="/contact-us" className="nav-item nav-link" onClick={closeNavbar}>Contact</Link>
            </div>
          </div>
        </div>
      </nav>

      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        @keyframes glow {
          0% { 
            box-shadow: 0 0 5px rgba(255, 87, 34, 0.4);
          }
          50% { 
            box-shadow: 0 0 20px rgba(255, 87, 34, 0.8), 0 0 30px rgba(255, 193, 7, 0.6);
          }
          100% { 
            box-shadow: 0 0 5px rgba(255, 87, 34, 0.4);
          }
        }

        @keyframes slideIn {
          0% { 
            transform: translateX(-20px);
            opacity: 0;
          }
          100% { 
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        @keyframes slideDown {
          0% { 
            transform: translateY(-100%);
            opacity: 0;
          }
          100% { 
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideUp {
          0% { 
            transform: translateY(0);
            opacity: 1;
          }
          100% { 
            transform: translateY(-100%);
            opacity: 0;
          }
        }

        /* Desktop Topbar Styles */
        .topbar-enhanced {
          box-shadow: 0 2px 8px rgba(255, 87, 34, 0.1);
          border-bottom: 1px solid rgba(255, 87, 34, 0.2);
        }

        .opening-hours-container {
          background: linear-gradient(135deg, #ff5722 0%, #ff9800 50%, #ffc107 100%);
          border-radius: 25px;
          padding: 10px 20px;
          animation: glow 3s ease-in-out infinite;
          position: relative;
          overflow: hidden;
          border: 2px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 4px 15px rgba(255, 87, 34, 0.3);
        }

        .opening-hours-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          animation: shimmer 4s ease-in-out infinite;
        }

        .hours-text {
          color: white;
          font-weight: 600;
          font-size: 0.95rem;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          animation: slideIn 1s ease-out;
          position: relative;
          z-index: 1;
        }

        .clock-icon {
          animation: pulse 2s ease-in-out infinite;
          color: #fff3e0;
          margin-right: 8px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }

        .status-badge {
          animation: pulse 2s ease-in-out infinite;
          display: inline-block;
          padding: 4px 10px;
          border-radius: 15px;
          font-size: 0.8em;
          font-weight: bold;
          margin-left: 10px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }

        .status-open {
          background: linear-gradient(135deg, #4caf50, #8bc34a);
          color: white;
        }

        .status-closed {
          background: linear-gradient(135deg, #f44336, #ff5722);
          color: white;
        }

        /* Contact Info Styles */
        .contact-info {
          background: #de3233;
          position: relative;
        }

        .contact-info::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 0;
          height: 0;
          border-left: 30px solid #fff8f0;
          border-top: 62px solid transparent;
          border-bottom: 62px solid transparent;
        }

        .contact-item {
          padding: 12px 0;
          border-right: 1px solid rgba(255,255,255,0.2);
        }

        .contact-item:last-child {
          border-right: none;
        }

        /* Mobile Notification Styles */
        .mobile-notification {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1050;
          background: linear-gradient(135deg, #ff5722 0%, #ff9800 50%, #ffc107 100%);
          color: white;
          padding: 8px 15px;
          font-size: 0.75rem;
          font-weight: 600;
          text-align: center;
          box-shadow: 0 2px 15px rgba(255, 87, 34, 0.4);
          animation: slideDown 0.5s ease-out;
          border-bottom: 2px solid rgba(255, 255, 255, 0.3);
        }

        .mobile-notification.hide {
          animation: slideUp 0.5s ease-out forwards;
        }

        .mobile-notification-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 100%;
        }

        .mobile-notification-text {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        }

        .mobile-notification-close {
          background: none;
          border: none;
          color: white;
          font-size: 1.2rem;
          cursor: pointer;
          padding: 0;
          margin-left: 10px;
          opacity: 0.8;
          transition: opacity 0.3s ease;
        }

        .mobile-notification-close:hover {
          opacity: 1;
        }

        .mobile-notification-icon {
          margin-right: 6px;
          animation: pulse 2s ease-in-out infinite;
        }

        .mobile-notification-status {
          font-size: 0.7rem;
          margin-left: 8px;
          padding: 2px 6px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.2);
        }

        /* Navbar Styles */
        .navbar-enhanced {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          background: white !important;
          transition: all 0.3s ease;
          margin-top: 0;
        }

        .navbar-brand {
          transition: transform 0.3s ease;
        }

        .navbar-brand:hover {
          transform: scale(1.05);
        }

        .navbar-nav .nav-link {
          font-weight: 500;
          color: #333 !important;
          margin: 0 10px;
          padding: 10px 15px !important;
          border-radius: 8px;
          transition: all 0.3s ease;
          position: relative;
        }

        .navbar-nav .nav-link:hover {
          background: linear-gradient(135deg, #ff5722, #dc3545);
          color: white !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(255, 87, 34, 0.3);
        }

        .navbar-nav .nav-link.active {
          background: linear-gradient(135deg, #ff5722, #dc3545);
          color: white !important;
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
        }

        /* Custom Navbar Toggler Styles */
        .navbar-toggler {
          border: 2px solid #ff5722;
          border-radius: 8px;
          transition: all 0.3s ease;
          padding: 8px 12px;
          background: transparent;
          position: relative;
          width: 50px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .navbar-toggler:hover {
          background: #ff5722;
          transform: scale(1.1);
        }

        .navbar-toggler:focus {
          box-shadow: 0 0 0 3px rgba(255, 87, 34, 0.25);
          outline: none;
        }

        /* Custom Hamburger Icon */
        .hamburger-icon {
          width: 24px;
          height: 18px;
          position: relative;
          transform: rotate(0deg);
          transition: 0.3s ease-in-out;
        }

        .hamburger-icon .bar {
          display: block;
          position: absolute;
          height: 3px;
          width: 100%;
          background: #ff5722;
          border-radius: 9px;
          opacity: 1;
          left: 0;
          transform: rotate(0deg);
          transition: 0.25s ease-in-out;
        }

        .navbar-toggler:hover .hamburger-icon .bar {
          background: white;
        }

        .hamburger-icon .bar:nth-child(1) {
          top: 0px;
        }

        .hamburger-icon .bar:nth-child(2) {
          top: 7px;
        }

        .hamburger-icon .bar:nth-child(3) {
          top: 14px;
        }

        /* Animated states */
        .hamburger-icon .bar-1-active {
          top: 7px;
          transform: rotate(135deg);
        }

        .hamburger-icon .bar-2-active {
          opacity: 0;
          left: -60px;
        }

        .hamburger-icon .bar-3-active {
          top: 7px;
          transform: rotate(-135deg);
        }

        /* Logo Styles */
        .logo-container {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .logo-img {
          height: 80px;
          transition: all 0.3s ease;
          filter: drop-shadow(0 2px 8px rgba(0,0,0,0.1));
        }

        .brand-text {
          color: #dc3545 !important;
          font-weight: 700;
          font-size: 1.5rem;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
          margin: 0;
        }
          
        /* Responsive Adjustments */
        @media (max-width: 991px) {
          .navbar-enhanced {
            margin-top: 0;
          }

          .logo-img {
            height: 60px;
          }

          .brand-text {
            font-size: 1.2rem;
          }

          .navbar-nav .nav-link {
            margin: 5px 0;
            text-align: center;
          }

          .mobile-notification {
            font-size: 0.7rem;
            padding: 6px 12px;
          }

          .mobile-notification-status {
            font-size: 0.6rem;
            padding: 1px 4px;
          }
        }

        @media (max-width: 576px) {
          .logo-img {
            height: 50px;
          }

          .brand-text {
            font-size: 1rem;
          }

          .contact-item {
            padding: 8px 0;
            font-size: 0.9rem;
          }

          .hours-text {
            font-size: 0.85rem;
          }

          .opening-hours-container {
            padding: 8px 15px;
          }

          .mobile-notification {
            font-size: 0.65rem;
            padding: 5px 10px;
          }

          .mobile-notification-text {
            font-size: 0.65rem;
          }

          .mobile-notification-status {
            font-size: 0.55rem;
            margin-left: 4px;
          }

          .mobile-notification-close {
            font-size: 1rem;
            margin-left: 5px;
          }
        }
      `}</style>

    </>
  )
}